import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import cake1 from '../assets/milestone1.png';
import hero1 from '../assets/milestone-hero1.svg';
import cake2 from '../assets/milestone2.png';
import hero2 from '../assets/milestone-hero2.svg';
import cake3 from '../assets/milestone3.png';
import hero3 from '../assets/milestone-hero3.svg';
import cake4 from '../assets/milestone4.png';
import hero4 from '../assets/milestone-hero4.svg';
import cake5 from '../assets/milestone5.png';
import hero5 from '../assets/milestone-hero5.svg';

const ScoreContext = createContext(null);

const MILESTONES = [
  { id: 'milestone-18', threshold: 18, message: 'Ура! У нашого торта готова основа!', cakeImg: cake1, heroImg: hero1 },
  { id: 'milestone-38', threshold: 38, message: 'Торт стає смачнішим!', cakeImg: cake2, heroImg: hero2 },
  { id: 'milestone-54', threshold: 54, message: 'Ого, який крутий торт виходить вже!', cakeImg: cake3, heroImg: hero3 },
  { id: 'milestone-62', threshold: 62, message: 'Торт вже вау! Це вже рівень майстра ✨', cakeImg: cake4, heroImg: hero4  },
  { id: 'milestone-90', threshold: 90, message: 'У тебе вийшов ідеальний торт! Ідеальний результат!', cakeImg: cake5, heroImg: hero5 }
];

export function ScoreProvider({ children }) {
  const [userAnswers, setUserAnswers] = useState({});
  const [attemptsByScreen, setAttemptsByScreen] = useState({});
  const [shownMilestones, setShownMilestones] = useState([]);
  const [activeMilestone, setActiveMilestone] = useState(null);
  const [pendingMilestones, setPendingMilestones] = useState([]);
  const prevTotalScoreRef = useRef(0);

  const totalScore = Object.values(userAnswers).reduce(
    (sum, value) => sum + Number(value || 0),
    0
  );

  const submitAnswer = (screenId, baseScore, isCorrect) => {
    if (screenId == null || baseScore == null) return;

    setAttemptsByScreen((prevAttempts) => {
      const attemptNumber = (prevAttempts[screenId] || 0) + 1;

      let awardedScore = 0;
      if (isCorrect) {
        if (attemptNumber === 1) {
          awardedScore = Number(baseScore);
        } else if (attemptNumber === 2) {
          awardedScore = Number(baseScore) / 2;
        }
      }

      setUserAnswers((prevAnswers) => ({
        ...prevAnswers,
        [screenId]: awardedScore
      }));

      return {
        ...prevAttempts,
        [screenId]: attemptNumber
      };
    });
  };

  const markMilestoneShown = (milestoneId) => {
    setShownMilestones((prev) => (
      prev.includes(milestoneId) ? prev : [...prev, milestoneId]
    ));
  };

  const dismissMilestone = () => {
    setActiveMilestone(null);
  };

  const activatePendingMilestone = () => {
    if (activeMilestone) return false;
    if (pendingMilestones.length === 0) return false;

    const [nextMilestone, ...rest] = pendingMilestones;
    setActiveMilestone(nextMilestone);
    setPendingMilestones(rest);
    return true;
  };

  const resetScore = () => {
    setUserAnswers({});
    setAttemptsByScreen({});
    setShownMilestones([]);
    setActiveMilestone(null);
    setPendingMilestones([]);
    prevTotalScoreRef.current = 0;
  };

  useEffect(() => {
    const prevTotalScore = prevTotalScoreRef.current;

    if (totalScore > prevTotalScore) {
      const newlyReached = MILESTONES.filter((milestone) => (
        prevTotalScore < milestone.threshold &&
        totalScore >= milestone.threshold &&
        !shownMilestones.includes(milestone.id) &&
        !pendingMilestones.some((item) => item.id === milestone.id) &&
        activeMilestone?.id !== milestone.id
      ));

      if (newlyReached.length > 0) {
        setPendingMilestones((prev) => {
          const knownIds = new Set(prev.map((item) => item.id));
          const nextQueue = [...prev];

          newlyReached.forEach((milestone) => {
            if (!knownIds.has(milestone.id)) {
              nextQueue.push(milestone);
            }
          });

          return nextQueue;
        });
        setShownMilestones((prev) => [
          ...prev,
          ...newlyReached
            .map((milestone) => milestone.id)
            .filter((id) => !prev.includes(id))
        ]);
      }
    }

    prevTotalScoreRef.current = totalScore;
  }, [activeMilestone, pendingMilestones, totalScore, shownMilestones]);

  return (
    <ScoreContext.Provider
      value={{
        userAnswers,
        attemptsByScreen,
        totalScore,
        shownMilestones,
        activeMilestone,
        milestones: MILESTONES,
        submitAnswer,
        markMilestoneShown,
        hasPendingMilestone: pendingMilestones.length > 0,
        activatePendingMilestone,
        dismissMilestone,
        resetScore
      }}
    >
      {children}
    </ScoreContext.Provider>
  );
}

export function useScore() {
  const context = useContext(ScoreContext);

  if (!context) {
    throw new Error('useScore must be used within a ScoreProvider');
  }

  return context;
}
